import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { pilatesContent } from '../../mocks/pilates-content';

export default function PilatesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 bg-white">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-reset-muted mb-10 font-semibold">
            REFORMER PILATES
          </p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-reset-dark mb-12 tracking-tight leading-relaxed-custom px-4 uppercase">
            Movement as Medicine
          </h1>
          <p className="text-base md:text-lg text-reset-dark leading-relaxed max-w-3xl mx-auto px-4">
            {pilatesContent.hero.description}
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              THE BENEFITS
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              Why Reformer Pilates?
            </h2>
            <p className="text-base text-reset-dark leading-relaxed max-w-2xl mx-auto">
              Reformer Pilates is more than exercise—it's a sustainable practice for long-term strength, mobility, and wellbeing.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {pilatesContent.benefits.map((benefit, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <i className={`${benefit.icon} text-4xl text-reset-blue w-10 h-10 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-lg font-bold text-reset-dark mb-4 uppercase tracking-wide">
                  {benefit.title}
                </h3>
                <p className="text-sm text-reset-dark leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-grey">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
                OUR APPROACH
              </p>
              <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
                Form Over Intensity
              </h2>
              <p className="text-base text-reset-dark leading-relaxed mb-6">
                {pilatesContent.philosophy.intro}
              </p>
              <p className="text-base text-reset-dark leading-relaxed mb-10">
                {pilatesContent.philosophy.detail}
              </p>
              <ul className="space-y-4">
                {pilatesContent.philosophy.principles.map((principle, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <i className="ri-check-line text-reset-blue text-xl mt-0.5 w-6 h-6 flex items-center justify-center flex-shrink-0"></i>
                    <span className="text-sm text-reset-dark leading-relaxed">
                      {principle}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-lg">
                <img
                  src="https://public.readdy.ai/ai/img_res/6e5d41f7677ef23b01f3d40cc0a18288.jpg"
                  alt="Reformer Pilates Studio"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Studio Features */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              THE STUDIO
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              Boutique by Design
            </h2>
            <p className="text-base text-reset-dark leading-relaxed max-w-2xl mx-auto">
              {pilatesContent.studio.description}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {pilatesContent.studio.features.map((feature, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <i className={`${feature.icon} text-4xl text-reset-blue`}></i>
                </div>
                <h3 className="text-lg font-bold text-reset-dark mb-4 uppercase tracking-wide">
                  {feature.title}
                </h3>
                <p className="text-sm text-reset-dark leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Class Types */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-grey">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              CLASS OFFERINGS
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              Designed for Depth
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pilatesContent.classes.map((classType, idx) => (
              <div key={idx} className="bg-white rounded-3xl p-10 shadow-sm">
                <h3 className="text-2xl font-bold text-reset-dark mb-4 uppercase tracking-wide">
                  {classType.name}
                </h3>
                <p className="text-sm text-reset-muted mb-6 uppercase tracking-wider">
                  {classType.duration} · {classType.level}
                </p>
                <p className="text-base text-reset-dark leading-relaxed mb-6">
                  {classType.description}
                </p>
                <ul className="space-y-3">
                  {classType.focus.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-reset-blue text-lg mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                      <span className="text-sm text-reset-dark leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instructors */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              OUR TEAM
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              Expert-Led Practice
            </h2>
            <p className="text-base text-reset-dark leading-relaxed max-w-2xl mx-auto">
              {pilatesContent.instructors.intro}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {pilatesContent.instructors.team.map((instructor, idx) => (
              <div key={idx} className="text-center">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden mb-6 shadow-sm">
                  <img
                    src={instructor.image}
                    alt={instructor.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h3 className="text-xl font-bold text-reset-dark mb-2 uppercase tracking-wide">
                  {instructor.name}
                </h3>
                <p className="text-sm text-reset-muted mb-4 uppercase tracking-wider">
                  {instructor.title}
                </p>
                <p className="text-sm text-reset-dark leading-relaxed">
                  {instructor.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Preview */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-grey">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              CLASS SCHEDULE
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              Find Your Rhythm
            </h2>
            <p className="text-base text-reset-dark leading-relaxed max-w-2xl mx-auto mb-10">
              {pilatesContent.schedule.intro}
            </p>
          </div>
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="space-y-6">
              {pilatesContent.schedule.sample.map((day, idx) => (
                <div key={idx} className="border-b border-reset-grey last:border-0 pb-6 last:pb-0">
                  <h3 className="text-lg font-bold text-reset-dark mb-4 uppercase tracking-wide">
                    {day.day}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {day.classes.map((classItem, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <span className="text-sm font-semibold text-reset-blue min-w-[60px]">
                          {classItem.time}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-reset-dark">
                            {classItem.name}
                          </p>
                          <p className="text-xs text-reset-muted mt-1">
                            {classItem.instructor}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a
                href="/#visit"
                className="inline-block bg-reset-blue text-white hover:bg-[#4A6278] px-8 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer uppercase shadow-sm hover:shadow-md"
              >
                View Full Schedule
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-reset-muted mb-8 font-semibold">
              YOUR FIRST CLASS
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-8 tracking-tight uppercase">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pilatesContent.firstClass.map((item, idx) => (
              <div key={idx} className="flex items-start gap-6">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-reset-grey rounded-full">
                  <span className="text-lg font-bold text-reset-blue">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-reset-dark mb-3 uppercase tracking-wide">
                    {item.title}
                  </h3>
                  <p className="text-sm text-reset-dark leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-reset-grey">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-reset-dark mb-10 tracking-tight uppercase">
            Begin Your Practice
          </h2>
          <p className="text-base text-reset-dark leading-relaxed mb-12 px-4">
            Join us for a class and experience movement as medicine. Members enjoy priority booking and consistent instructor relationships.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block bg-reset-blue text-white hover:bg-[#4A6278] px-8 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer uppercase shadow-sm hover:shadow-md"
            >
              Plan Your Visit
            </a>
            <a
              href="/pricing"
              className="inline-block bg-white text-reset-dark hover:bg-reset-grey border-2 border-reset-dark px-8 py-4 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer uppercase"
            >
              Explore Memberships
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
